import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Timeline, DataSet } from 'vis-timeline/standalone'
import 'vis-timeline/styles/vis-timeline-graph2d.css'
import { toDayOfYear, fromDayOfYear, formatDate, parseDate } from '../../lib/harptos'
import { CATEGORIES, getCategory } from '../../lib/categories'
import './CampaignTimeline.css'

const MS_PER_DAY = 24 * 60 * 60 * 1000

// Convert Harptos date to JS Date, keeping years aligned (1492 DR = year 1492)
function harptosToDate(dateObj) {
  if (!dateObj) return null
  const dayOfYear = toDayOfYear(dateObj.month, dateObj.day || 1, dateObj.year)
  return new Date(dateObj.year, 0, dayOfYear)
}

// Convert JS Date back to Harptos date (for axis labels)
// vis-timeline may pass Date objects, timestamps, or moment-like objects
function dateToHarptos(dateInput) {
  // Ensure we have a proper Date object
  let date
  if (dateInput instanceof Date) {
    date = dateInput
  } else if (typeof dateInput === 'number') {
    date = new Date(dateInput)
  } else if (dateInput && typeof dateInput.toDate === 'function') {
    // Handle moment.js or similar objects
    date = dateInput.toDate()
  } else if (dateInput && typeof dateInput.valueOf === 'function') {
    date = new Date(dateInput.valueOf())
  } else {
    // Fallback: try to construct a date
    date = new Date(dateInput)
  }

  // Validate the date
  if (isNaN(date.getTime())) {
    return { month: 'Hammer', day: 1, year: 1492, isSpecial: false }
  }

  const year = date.getFullYear()
  const startOfYear = new Date(year, 0, 1)
  const dayOfYear = Math.floor((date - startOfYear) / MS_PER_DAY) + 1
  return fromDayOfYear(Math.max(1, Math.min(dayOfYear, 366)), year)
}

// Get abbreviated Harptos month name for axis labels
function getHarptosMonthAbbrev(harptosDate) {
  if (!harptosDate) return ''
  const { month, isSpecial } = harptosDate

  if (isSpecial) {
    const abbrevs = {
      'Midwinter': 'Midw',
      'Greengrass': 'Grgrss',
      'Midsummer': 'Midsm',
      'Shieldmeet': 'Shld',
      'Highharvesttide': 'Highhrvst',
      'Feast of the Moon': 'Feast'
    }
    return abbrevs[month] || month.slice(0, 4)
  }

  const abbrevs = {
    'Hammer': 'Ham',
    'Alturiak': 'Altr',
    'Ches': 'Ches',
    'Tarsakh': 'Tars',
    'Mirtul': 'Mirt',
    'Kythorn': 'Kyth',
    'Flamerule': 'Flam',
    'Eleasis': 'Eles',
    'Eleint': 'Elnt',
    'Marpenoth': 'Marp',
    'Uktar': 'Ukt',
    'Nightal': 'Nght'
  }
  return abbrevs[month] || month.slice(0, 4)
}

function formatDateRange(start, end) {
  const startStr = formatDate(start)
  if (!end) return startStr
  const endStr = formatDate(end)
  return `${startStr} → ${endStr}`
}

export default function CampaignTimeline({ events, activeFilters, onFilterChange, characters = [] }) {
  // Generate dynamic styles for categories
  const categoryStyles = useMemo(() => {
    return CATEGORIES.map(cat => `
      .vis-item.category-${cat.id} {
        background: ${cat.color} !important;
        border-color: ${cat.borderColor} !important;
        ${cat.style === 'dashed' ? 'border-style: dashed !important;' : ''}
        ${cat.style === 'italic' ? 'font-style: italic !important;' : ''}
      }
      .vis-item.category-${cat.id}:hover {
        background: ${cat.hoverColor} !important;
      }
      .filter-button.category-${cat.id}.active {
        background: ${cat.color};
        border-color: ${cat.borderColor};
      }
    `).join('\n')
  }, [])

  const containerRef = useRef(null)
  const timelineRef = useRef(null)
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, event: null })
  const [selectedYear, setSelectedYear] = useState(null)

  // Extract unique years from events
  const availableYears = useMemo(() => {
    const years = new Set()
    events.forEach(event => {
      if (event.start?.year) years.add(event.start.year)
      if (event.end?.year) years.add(event.end.year)
    })
    return Array.from(years).sort((a, b) => a - b)
  }, [events])

  // Convert events to vis-timeline format
  const getTimelineItems = useCallback(() => {
    const filtered = activeFilters.length === 0
      ? events
      : events.filter(e => activeFilters.includes(e.category))

    return filtered.map(event => {
      const startDate = parseDate(event.start)
      const endDate = event.end ? parseDate(event.end) : null

      const item = {
        id: event.id,
        content: event.title,
        start: harptosToDate(startDate),
        className: `category-${event.category}`,
        _data: {
          ...event,
          startDate,
          endDate,
          // Get linked character names
          characters: (event.characterIds || [])
            .map(id => characters.find(c => c.id === id))
            .filter(Boolean)
        }
      }

      if (endDate) {
        item.end = harptosToDate(endDate)
        item.type = 'range'
      } else {
        item.type = 'box'
      }

      return item
    })
  }, [events, activeFilters, characters])

  // Initialize timeline
  useEffect(() => {
    if (!containerRef.current) return

    const items = new DataSet(getTimelineItems())

    const options = {
      height: '100%',
      min: harptosToDate({ month: 'Hammer', day: 1, year: 1490 }),
      max: harptosToDate({ month: 'Nightal', day: 30, year: 1500 }),
      zoomMin: MS_PER_DAY * 7,
      zoomMax: MS_PER_DAY * 365 * 5,
      orientation: 'top',
      showCurrentTime: false,
      stack: true,
      stackSubgroups: true,
      margin: {
        item: { horizontal: 10, vertical: 5 }
      },
      format: {
        minorLabels: (date) => {
          const harptos = dateToHarptos(date)
          if (harptos.isSpecial) return '✦'
          return harptos.day ? harptos.day.toString() : ''
        },
        majorLabels: (date) => {
          const harptos = dateToHarptos(date)
          const monthAbbrev = getHarptosMonthAbbrev(harptos)
          return `${monthAbbrev} ${harptos.year} DR`
        }
      }
    }

    const timeline = new Timeline(containerRef.current, items, options)
    timelineRef.current = timeline

    timeline.fit({ animation: { duration: 500 } })

    timeline.on('itemover', (props) => {
      if (!props.item) return
      const item = items.get(props.item)
      if (!item?._data) return

      const rect = containerRef.current.getBoundingClientRect()
      setTooltip({
        visible: true,
        x: props.event.clientX - rect.left + 15,
        y: props.event.clientY - rect.top + 15,
        event: item._data
      })
    })

    timeline.on('itemout', () => {
      setTooltip(prev => ({ ...prev, visible: false }))
    })

    return () => {
      timeline.destroy()
    }
  }, [])

  // Update items when filters or events change
  useEffect(() => {
    if (!timelineRef.current) return
    const items = new DataSet(getTimelineItems())
    timelineRef.current.setItems(items)
  }, [activeFilters, events, getTimelineItems])

  const handleZoom = (direction) => {
    if (!timelineRef.current) return
    const range = timelineRef.current.getWindow()
    const interval = range.end - range.start
    const newInterval = direction === 'in' ? interval * 0.5 : interval * 2
    const center = (range.start.getTime() + range.end.getTime()) / 2

    timelineRef.current.setWindow(
      new Date(center - newInterval / 2),
      new Date(center + newInterval / 2),
      { animation: { duration: 300 } }
    )
  }

  const handleFit = () => {
    if (!timelineRef.current) return
    timelineRef.current.fit({ animation: { duration: 500 } })
    setSelectedYear(null)
  }

  const goToYear = (year) => {
    if (!timelineRef.current || !year) return
    setSelectedYear(year)

    // Set window to show the full year
    const startDate = harptosToDate({ month: 'Hammer', day: 1, year })
    const endDate = harptosToDate({ month: 'Nightal', day: 30, year })

    timelineRef.current.setWindow(startDate, endDate, { animation: { duration: 400 } })
  }

  const toggleFilter = (category) => {
    if (activeFilters.includes(category)) {
      onFilterChange(activeFilters.filter(f => f !== category))
    } else {
      onFilterChange([...activeFilters, category])
    }
  }

  return (
    <div className="timeline-section">
      {/* Dynamic category styles */}
      <style>{categoryStyles}</style>

      {/* Filter bar */}
      <div className="filter-bar">
        <span className="filter-label">Filter:</span>
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={`filter-button category-${cat.id} ${activeFilters.includes(cat.id) ? 'active' : ''}`}
            onClick={() => toggleFilter(cat.id)}
          >
            {cat.shortLabel}
          </button>
        ))}
        {activeFilters.length > 0 && (
          <button className="filter-button filter-clear" onClick={() => onFilterChange([])}>
            Clear
          </button>
        )}

        <div className="filter-separator" />

        <span className="filter-label">Year:</span>
        {availableYears.map(year => (
          <button
            key={year}
            className={`filter-button year-button ${selectedYear === year ? 'active' : ''}`}
            onClick={() => goToYear(year)}
          >
            {year} DR
          </button>
        ))}
        {selectedYear && (
          <button className="filter-button filter-clear" onClick={handleFit}>
            All
          </button>
        )}
      </div>

      {/* Timeline */}
      <div className="timeline-wrapper">
        <div ref={containerRef} style={{ height: '100%' }} />

        {/* Zoom controls */}
        <div className="zoom-controls">
          <button className="zoom-button" onClick={() => handleZoom('in')} title="Zoom In">+</button>
          <button className="zoom-button" onClick={() => handleZoom('out')} title="Zoom Out">−</button>
          <button className="zoom-button" onClick={handleFit} title="Fit All">⊡</button>
        </div>

        {/* Tooltip */}
        {tooltip.event && (
          <div
            className={`event-tooltip ${tooltip.visible ? 'visible' : ''}`}
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            <h3>{tooltip.event.title}</h3>
            <div className="tooltip-date">
              {formatDateRange(tooltip.event.startDate, tooltip.event.endDate)}
            </div>
            {tooltip.event.location && (
              <div className="tooltip-location">{tooltip.event.location}</div>
            )}
            {tooltip.event.description && (
              <div className="tooltip-description">{tooltip.event.description}</div>
            )}
            {tooltip.event.characters?.length > 0 && (
              <div className="tooltip-characters">
                {tooltip.event.characters.map(c => c.name).join(', ')}
              </div>
            )}
            <div className="tooltip-category">
              {getCategory(tooltip.event.category).label}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
